import axios from "axios";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
// In production the frontend and backend share the same origin (the platform
// proxies /api/* to the FastAPI backend). Some preview URLs 307-redirect to an
// internal hostname which makes a fixed REACT_APP_BACKEND_URL trigger an
// avoidable cross-origin hop. Prefer the page's origin when running in the
// browser; fall back to env for SSR / Node contexts.
const RESOLVED_BACKEND =
    typeof window !== "undefined" && window.location && window.location.origin
        ? window.location.origin
        : BACKEND_URL;
export const API = `${RESOLVED_BACKEND}/api`;

const TOKEN_KEY = "vc_admin_token";
export const setAdminToken = (t) => {
    if (t) localStorage.setItem(TOKEN_KEY, t);
    else localStorage.removeItem(TOKEN_KEY);
};
export const getAdminToken = () => localStorage.getItem(TOKEN_KEY) || null;

export const api = axios.create({
    baseURL: API,
    headers: { "Content-Type": "application/json" },
});

// Inject Authorization header when an admin token is present
api.interceptors.request.use((config) => {
    const t = getAdminToken();
    if (t) {
        config.headers = config.headers || {};
        config.headers.Authorization = `Bearer ${t}`;
    }
    return config;
});

export const submitApplication = (payload) =>
    api.post("/applications", payload).then((r) => r.data);

export const fetchApplicationStatus = (refId) =>
    api.get(`/applications/${encodeURIComponent(refId)}`).then((r) => r.data);

export const CITIES = [
    "Bengaluru",
    "Paris",
    "Sydney",
    "Dubai",
    "New York",
    "London",
    "Miami",
    "San Francisco",
    "Singapore",
    "Berlin",
];

// ---- Sphere Gallery ----
export const fetchTiles = () => api.get("/tiles").then((r) => r.data);

export const patchTile = (id, payload) =>
    api.patch(`/tiles/${id}`, payload).then((r) => r.data);

export const uploadTileImage = (file) => {
    const fd = new FormData();
    fd.append("file", file);
    return api
        .post("/admin/upload", fd, {
            headers: { "Content-Type": "multipart/form-data" },
        })
        .then((r) => r.data);
};

// ---- Admin auth ----
export const fetchAuthMe = () => api.get("/auth/me").then((r) => r.data);

export const completeAuthSession = async (sessionId) => {
    const res = await api.post("/auth/session-callback", {
        session_id: sessionId,
    });
    if (res.data?.session_token) setAdminToken(res.data.session_token);
    return res.data;
};

export const logoutAdmin = async () => {
    try {
        await api.post("/auth/logout");
    } finally {
        setAdminToken(null);
    }
    return { ok: true };
};

// ---- Admin: applicant management ----
export const adminListApplications = (params = {}) =>
    api
        .get("/admin/applications", { params })
        .then((r) => r.data);

export const adminGetApplication = (refId) =>
    api
        .get(`/admin/applications/${encodeURIComponent(refId)}`)
        .then((r) => r.data);

export const adminPatchApplication = (refId, payload) =>
    api
        .patch(`/admin/applications/${encodeURIComponent(refId)}`, payload)
        .then((r) => r.data);

export const adminExportApplicationsUrl = (params = {}) => {
    const search = new URLSearchParams(
        Object.entries(params).filter(([_, v]) => v != null && v !== ""),
    ).toString();
    return `${API}/admin/applications/export${search ? "?" + search : ""}`;
};

export const adminDownloadApplicationsCSV = async (params = {}) => {
    const r = await api.get("/admin/applications/export", {
        params,
        responseType: "blob",
    });
    const url = URL.createObjectURL(r.data);
    const a = document.createElement("a");
    a.href = url;
    a.download = `vibecity-applications-${new Date().toISOString().slice(0, 10)}.csv`;
    document.body.appendChild(a);
    a.click();
    a.remove();
    setTimeout(() => URL.revokeObjectURL(url), 500);
};

// ---- City Cards (orbital) ----
export const fetchCityCards = () =>
    api.get("/city-cards").then((r) => r.data);

export const patchCityCard = (id, payload) =>
    api.patch(`/city-cards/${id}`, payload).then((r) => r.data);

// ---- Playbook cards (Expectations section) ----
export const fetchPlaybookCards = () =>
    api.get("/playbook-cards").then((r) => r.data);

export const patchPlaybookCard = (id, payload) =>
    api.patch(`/playbook-cards/${id}`, payload).then((r) => r.data);

// ---- Generic admin assets (image bank: perk icons, etc.) ----
export const fetchAssets = () => api.get("/assets").then((r) => r.data);

export const patchAsset = (id, payload) =>
    api.patch(`/assets/${id}`, payload).then((r) => r.data);

// ---- Email events (admin per-application timeline) ----
export const fetchEmailEvents = (refId) =>
    api.get(`/admin/applications/${refId}/email-events`).then((r) => r.data);

// ---- Admin emails hub (global activity + send + broadcast + templates) ----
export const fetchEmailTemplates = () =>
    api.get("/admin/emails/templates").then((r) => r.data);
export const fetchEmailTemplate = (key) =>
    api.get(`/admin/emails/templates/${key}`).then((r) => r.data);
export const saveEmailTemplateOverride = (key, payload) =>
    api.put(`/admin/emails/templates/${key}`, payload).then((r) => r.data);
export const deleteEmailTemplateOverride = (key) =>
    api.delete(`/admin/emails/templates/${key}`).then((r) => r.data);
export const previewEmail = (payload) =>
    api.post("/admin/emails/preview", payload).then((r) => r.data);
export const fetchEmailActivity = (params = {}) =>
    api.get("/admin/emails/events", { params }).then((r) => r.data);
export const fetchEmailStats = (days = 30) =>
    api.get("/admin/emails/stats", { params: { days } }).then((r) => r.data);
export const sendCustomEmail = (payload) =>
    api.post("/admin/emails/send-custom", payload).then((r) => r.data);
export const broadcastEmail = (payload) =>
    api.post("/admin/emails/broadcast", payload).then((r) => r.data);
export const fetchBroadcasts = (limit = 25) =>
    api.get("/admin/emails/broadcasts", { params: { limit } }).then((r) => r.data);

// ---- Host events (open program) ----
export const submitHostEvent = (data) =>
    api.post("/host-events", data).then((r) => r.data);
export const fetchHostEvent = (refId) =>
    api.get(`/host-events/${encodeURIComponent(refId)}`).then((r) => r.data);
export const adminListHostEvents = (params = {}) =>
    api.get("/admin/host-events", { params }).then((r) => r.data);
export const adminPatchHostEvent = (refId, payload) =>
    api.patch(`/admin/host-events/${refId}`, payload).then((r) => r.data);

// ---- Cohorts ----
export const fetchCohorts = () =>
    api.get("/admin/cohorts").then((r) => r.data);
export const createCohort = (payload) =>
    api.post("/admin/cohorts", payload).then((r) => r.data);
export const patchCohort = (id, payload) =>
    api.patch(`/admin/cohorts/${id}`, payload).then((r) => r.data);
export const deleteCohort = (id) =>
    api.delete(`/admin/cohorts/${id}`).then((r) => r.data);
export const previewCohort = (id) =>
    api.post(`/admin/cohorts/${id}/preview`).then((r) => r.data);
export const applyCohortFilters = (id) =>
    api.post(`/admin/cohorts/${id}/apply`).then((r) => r.data);
export const assignToCohort = (id, refs) =>
    api
        .post(`/admin/cohorts/${id}/assign`, { reference_ids: refs })
        .then((r) => r.data);
export const removeFromCohort = (id, refs) =>
    api
        .post(`/admin/cohorts/${id}/remove`, { reference_ids: refs })
        .then((r) => r.data);
export const fetchCohortMembers = (id) =>
    api.get(`/admin/cohorts/${id}/members`).then((r) => r.data);
